import { ReactNode, useCallback, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Banner, Text as FplText } from '@figma/fpl-components';
import { Box } from '@/UI/Box/Box';
import { Typography, type TypographyProps } from '@/UI/AppTypography/AppTypography';
import { themeColor } from '@/UI/themeColors';
import { I18N_KEYS } from '@/language/keys';
import { WarningCircleIcon } from '@/UI/Icons';
import { Flex, FlexCenVer } from '@/UI/styles';
import { useSubscriptionPermissions } from '@/hooks/useSubscriptionPermissions/useSubscriptionPermissions';
import { SubscriptionType } from '@/types/shared';
import { ButtonTextPlain } from '@/UI/ButtonTextPlain/ButtonTextPlain';
import { CreditsContext } from '@/services/CreditsContext';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import type { PricingPageViewAnalyticsContext } from '@/enums/pricing-page.enum';

const Text = ({ children, variant }: { children: ReactNode; variant: TypographyProps['variant'] }) => (
  <Typography variant={variant} color={themeColor.white100}>
    {children}
  </Typography>
);

const Btn = ({
  onClick,
  children,
  variant,
}: {
  onClick: () => void;
  children: ReactNode;
  variant: TypographyProps['variant'];
}) => (
  <ButtonTextPlain onClick={onClick} variant={variant} color={themeColor.white100}>
    {children}
  </ButtonTextPlain>
);

const LowCreditsMsg = ({
  analyticsContext,
  isSingleLine = false,
}: {
  analyticsContext: PricingPageViewAnalyticsContext;
  isSingleLine?: boolean;
}) => {
  const { t } = useTranslation();
  const { handleGetMoreCreditsClick, openUpgradeModalFromEntrypoint } = useContext(CreditsContext);
  const { isSubscriptionTypeIn, isAllowedForPermission } = useSubscriptionPermissions();
  const isFplMigrationEnabled = useFplMigrationFlag();
  const isFreePlan = isSubscriptionTypeIn([SubscriptionType.Free]);
  const canManageCredits = isAllowedForPermission('credits', 'action');
  const handleOpenUpgradeModal = useCallback(() => {
    openUpgradeModalFromEntrypoint(analyticsContext);
  }, [analyticsContext, openUpgradeModalFromEntrypoint]);

  const getTextComponent = useMemo(() => {
    const textVariant = isSingleLine ? 'body-sm-rg' : 'body-xs-rg';
    if (isFreePlan) {
      return (
        <>
          <Text variant={textVariant}>
            {t(I18N_KEYS.RECIPE_MAIN.NODES.MODEL_BASE_NODE.MODEL_NODE_FOOTER.CREDITS_MESSAGE.LOW_CREDITS_MESSAGE_SHORT)}
          </Text>
          &nbsp;
          <Btn onClick={handleOpenUpgradeModal} variant={textVariant}>
            {t(
              I18N_KEYS.RECIPE_MAIN.NODES.MODEL_BASE_NODE.MODEL_NODE_FOOTER.CREDITS_MESSAGE
                .LOW_CREDITS_MESSAGE_PART_2_FREE,
            )}
          </Btn>
        </>
      );
    } else {
      if (canManageCredits) {
        return (
          <>
            <Text variant={textVariant}>
              {t(
                I18N_KEYS.RECIPE_MAIN.NODES.MODEL_BASE_NODE.MODEL_NODE_FOOTER.CREDITS_MESSAGE.LOW_CREDITS_MESSAGE_SHORT,
              )}
            </Text>
            &nbsp;
            <Btn onClick={handleGetMoreCreditsClick} variant={textVariant}>
              {t(
                I18N_KEYS.RECIPE_MAIN.NODES.MODEL_BASE_NODE.MODEL_NODE_FOOTER.CREDITS_MESSAGE
                  .LOW_CREDITS_MESSAGE_PART_2_GET_MORE_CREDITS_CTA,
              )}
            </Btn>
            &nbsp;
            <Text variant={textVariant}>
              {t(I18N_KEYS.RECIPE_MAIN.NODES.MODEL_BASE_NODE.MODEL_NODE_FOOTER.CREDITS_MESSAGE.LOW_CREDITS_MESSAGE_OR)}
            </Text>
            &nbsp;
            <Btn onClick={handleOpenUpgradeModal} variant={textVariant}>
              {t(
                I18N_KEYS.RECIPE_MAIN.NODES.MODEL_BASE_NODE.MODEL_NODE_FOOTER.CREDITS_MESSAGE
                  .LOW_CREDITS_MESSAGE_PART_3_UPGRADE_CTA,
              )}
            </Btn>
          </>
        );
      } else {
        // view permission goes here
        return (
          <Text variant={textVariant}>
            {isSingleLine
              ? t(
                  I18N_KEYS.RECIPE_MAIN.NODES.MODEL_BASE_NODE.MODEL_NODE_FOOTER.CREDITS_MESSAGE
                    .LOW_CREDITS_MESSAGE_SHORT,
                )
              : t(
                  I18N_KEYS.RECIPE_MAIN.NODES.MODEL_BASE_NODE.MODEL_NODE_FOOTER.CREDITS_MESSAGE
                    .LOW_CREDITS_MESSAGE_SHORT,
                )}
            &nbsp;
            {t(
              I18N_KEYS.RECIPE_MAIN.NODES.MODEL_BASE_NODE.MODEL_NODE_FOOTER.CREDITS_MESSAGE
                .LOW_CREDITS_MESSAGE_PART_2_MEMBER,
            )}
          </Text>
        );
      }
    }
  }, [canManageCredits, handleGetMoreCreditsClick, handleOpenUpgradeModal, isFreePlan, isSingleLine, t]);

  if (isFplMigrationEnabled) {
    return (
      <Flex sx={{ width: '100%' }}>
        <Banner.Inset variant="default">
          <Banner.Message>
            {t(I18N_KEYS.RECIPE_MAIN.NODES.MODEL_BASE_NODE.MODEL_NODE_FOOTER.CREDITS_MESSAGE.LOW_CREDITS_MESSAGE_SHORT)}{' '}
            {isFreePlan ? (
              <Banner.ButtonLink onClick={handleOpenUpgradeModal}>
                <FplText.span variant="body-medium" color="inherit">
                  {t(
                    I18N_KEYS.RECIPE_MAIN.NODES.MODEL_BASE_NODE.MODEL_NODE_FOOTER.CREDITS_MESSAGE
                      .LOW_CREDITS_MESSAGE_PART_2_FREE,
                  )}
                </FplText.span>
              </Banner.ButtonLink>
            ) : canManageCredits ? (
              <>
                <Banner.ButtonLink onClick={handleGetMoreCreditsClick}>
                  {t(
                    I18N_KEYS.RECIPE_MAIN.NODES.MODEL_BASE_NODE.MODEL_NODE_FOOTER.CREDITS_MESSAGE
                      .LOW_CREDITS_MESSAGE_PART_2_GET_MORE_CREDITS_CTA,
                  )}
                </Banner.ButtonLink>{' '}
                {t(
                  I18N_KEYS.RECIPE_MAIN.NODES.MODEL_BASE_NODE.MODEL_NODE_FOOTER.CREDITS_MESSAGE.LOW_CREDITS_MESSAGE_OR,
                )}{' '}
                <Banner.ButtonLink onClick={handleOpenUpgradeModal}>
                  <FplText.span variant="body-medium" color="inherit">
                    {t(
                      I18N_KEYS.RECIPE_MAIN.NODES.MODEL_BASE_NODE.MODEL_NODE_FOOTER.CREDITS_MESSAGE
                        .LOW_CREDITS_MESSAGE_PART_3_UPGRADE_CTA,
                    )}
                  </FplText.span>
                </Banner.ButtonLink>
              </>
            ) : (
              t(
                I18N_KEYS.RECIPE_MAIN.NODES.MODEL_BASE_NODE.MODEL_NODE_FOOTER.CREDITS_MESSAGE
                  .LOW_CREDITS_MESSAGE_PART_2_MEMBER,
              )
            )}
          </Banner.Message>
        </Banner.Inset>
      </Flex>
    );
  }

  if (isSingleLine) {
    return (
      <Flex
        sx={{
          gap: 0.75,
          backgroundColor: themeColor.white16_T,
          py: 0.5,
          px: 0.75,
          borderRadius: 1,
          width: '100%',
        }}
      >
        <WarningCircleIcon color={themeColor.white100} />
        <FlexCenVer>{getTextComponent}</FlexCenVer>
      </Flex>
    );
  }

  return (
    <Flex
      sx={{
        gap: 0.75,
        backgroundColor: themeColor.white16_T,
        py: 0.5,
        px: 0.75,
        borderRadius: 1,
        height: 34,
        width: '100%',
      }}
    >
      <FlexCenVer>
        <WarningCircleIcon color={themeColor.white100} width={16} height={16} />
      </FlexCenVer>
      {/* Do not remove fontSize - hack to make multiple spans fit as single text */}
      <Box sx={{ fontSize: '10px' }}>{getTextComponent}</Box>
    </Flex>
  );
};

export default LowCreditsMsg;
